// Numeri del preventivo condomini. Gli importi arrivano come stringhe decimali dal lato del cliente
// ("-28.57" = paga meno). Il segno c'è sempre: «−» (U+2212) paga meno, «+» paga di più (spec § 7.4).
import type { AltroAddebito, BollettaVista, Condominio, Esito, OffertaMenu, SpreadFascia, Vista } from "@/api/preventivo";
import { arrotonda, euro as euroPortale, num, prezzoUnitario } from "@/lib/analisiScritte";

const MENO = "−";

/** Spazio non separabile fra una cifra e «€» / «c€» (anche «€/kWh»): nelle celle strette la cifra non va a capo
 *  lasciando l'unità da sola (revisione 1, M7). */
export function conUnita(s: string): string {
  return s.replace(/(\d) (?=c?€)/g, "$1\u00a0");
}

/** Preposizioni e congiunzioni: minuscole quando non sono la prima parola (R35). «dell'» si guarda prima dell'apostrofo. */
const MINUSCOLE = new Set(["di", "del", "della", "dell", "dei", "degli", "delle", "da", "dal", "dalla", "in", "a", "al",
  "alla", "e", "ed", "per", "con", "su", "tra", "fra"]);
/** Sigle del settore che restano in maiuscolo (R35). */
const SIGLE = new Set(["PUN", "PCV", "IVA", "ARERA", "AGSM", "AIM", "E.ON", "SPA", "SRL"]);
/** Numeri romani delle vie e dei nomi («XX Settembre», «Pio XI»), fino a 89: senza C, D e M non si confondono con parole. */
const ROMANO = /^(?=[XLVI])(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/;

/** Un nome scritto dalla bolletta tutto in maiuscolo («CONDOMINIO VIA DELLA PACE 15/A») con le iniziali maiuscole
 *  («Condominio Via della Pace 15/A»); scritto già con le minuscole resta com'è (R33, corretta con R35):
 *  - serve almeno una parola di tre lettere: un POD («IT001E…») non si tocca;
 *  - preposizioni e congiunzioni minuscole, salvo la prima parola; un'iniziale puntata («A. Volta») e una lettera
 *    dopo una parola puntata («Pal. A») restano;
 *  - restano come sono i numeri romani, le sigle del settore, i codici (una parola con le cifre e un solo gruppo di
 *    lettere: «ELF-610359», «B1», «3F») e le lettere sole («S.R.L.», la «A» di «Pal. A»). */
export function iniziali(s: string): string {
  if (s !== s.toUpperCase() || !/\p{L}{3,}/u.test(s)) return s;
  let prima = true;
  let puntata = false;
  return s.split(/(\s+)/).map((parola) => {
    if (parola.trim() === "") return parola;
    // dopo una parola puntata («PAL.», «SC.») una lettera sola è una sigla («Pal. A»), non una preposizione;
    // «L. DA VINCI» resta «L. da Vinci»
    const eraPrima = prima, dopoPunto = puntata;
    prima = false;
    puntata = parola.endsWith(".");
    // il cuore della parola, senza virgolette, parentesi o virgole attorno (il punto resta: «S.P.A.», «E.ON»)
    const [, apre, cuore, chiude] = parola.match(/^([^\p{L}\d]*)(.*?)([^\p{L}\d.]*)$/u)!;
    const gruppi = cuore.match(/\p{L}+/gu) ?? [];
    if (SIGLE.has(cuore) || ROMANO.test(cuore) || (/\d/.test(cuore) && gruppi.length <= 1)) return parola;
    if (!eraPrima && !(dopoPunto && cuore.length === 1) && !cuore.includes(".")) {
      // «DELL'ACQUA» → «dell'Acqua»; «DI» → «di»
      const [testa, ...resto] = cuore.split(/(?<=['’])/);
      if (MINUSCOLE.has(testa.toLowerCase().replace(/['’]$/, ""))) {
        return apre + testa.toLowerCase() + resto.map(maiuscola).join("") + chiude;
      }
    }
    return apre + maiuscola(cuore) + chiude;
  }).join("");
}

/** La vista come arriva dal server, coi nomi dei condomìni (nome, venditore, offerta attuale) passati da `iniziali`. Si
 *  applica una volta sola, all'arrivo della vista, così scheda, tabella, pannello, coda e riquadri scrivono il nome
 *  allo stesso modo (R35). Non tocca la vista ricevuta. */
export function nomiDellaVista(v: Vista): Vista {
  const nome = (s: string | null) => (s === null ? null : iniziali(s));
  return {
    ...v,
    condomini: v.condomini.map((c) => ({
      ...c, titolo: iniziali(c.titolo), venditore: nome(c.venditore), offerta_attuale: nome(c.offerta_attuale),
      // la chiave del gas c'è solo col gas acceso: senza, la vista resta com'era
      ...(c.venditore_gas !== undefined ? { venditore_gas: nome(c.venditore_gas) } : {}),
    })),
  };
}

/** Ogni gruppo di almeno due lettere con l'iniziale maiuscola; le lettere sole restano come sono. */
function maiuscola(s: string): string {
  return s.replace(/\p{L}{2,}/gu, (g) => g[0] + g.slice(1).toLowerCase());
}

/** «−28,57 €», «+2,22 €», «0,00 €»; «—» se il dato non c'è. */
export function euro(x: string | null): string {
  if (x === null) return "—";
  const s = euroPortale(x); // arrotonda sulle cifre, mai sul float; "-" solo se non è zero
  if (s.startsWith("-")) return MENO + s.slice(1);
  return s.startsWith("0,00 ") ? s : `+${s}`;
}

export function alMese(x: string): string {
  return `${euro(x)} al mese`;
}

/** «−343 € l'anno, a consumi uguali»: all'euro, mezzo verso l'alto sul valore assoluto, sulle cifre. Senza «circa»
 *  (Alfonso 29/10: in un preventivatore si mette il numero). */
export function anno(x: string): string {
  const p = arrotonda(x, 0);
  const nullo = /^0*$/.test(p.intero);
  const segno = nullo ? "" : p.neg ? MENO : "+";
  return conUnita(`${segno}${num({ intero: p.intero, dec: "" })} € l'anno, a consumi uguali`);
}

/** «−44 € l'anno»: la stima annua senza «a consumi uguali», per la riga del condominio della tabella, dove sta su
 *  una riga accanto alla cifra al mese (mockup «risparmio», variante 1); le schede tengono la frase intera. */
export function annoBreve(x: string): string {
  return anno(x).replace(/, a consumi uguali$/, "");
}

export function tono(x: string | null): "meno" | "piu" | "zero" {
  if (x === null) return "zero";
  const s = euro(x);
  return s.startsWith(MENO) ? "meno" : s.startsWith("+") ? "piu" : "zero";
}

/** Classe del colore per il tono: il colore accompagna il segno, non lo sostituisce. */
export const COLORE_TONO = { meno: "text-brand-dark", piu: "text-red-800", zero: "text-muted-foreground" } as const;

/** «su 6 delle 8 forniture» solo se il totale non le conta tutte. */
export function suFornitture(contati: number, totali: number): string | null {
  return contati === totali ? null : `su ${contati} delle ${totali} forniture`;
}

/** Cifra con al più `n` decimali, senza zeri inutili: «20», «3,5». Arrotonda sulle cifre come `euro`. */
function corta(x: string, n = 2): string {
  const p = arrotonda(x, n);
  const intero = p.intero.replace(/^0+(?=\d)/, "");
  return `${p.neg ? "-" : ""}${num({ intero, dec: p.dec.replace(/0+$/, "") })}`;
}

/** «20 €/mese + 0,035 €/kWh»: l'offerta scelta con le unità delle colonne PCV e Spread dichiarato (spec § 8); il nome
 *  della CTE se mancano quota o spread. */
export function nomeBreve(o: OffertaMenu): string {
  if (o.quota === null || o.spread === null) return o.nome;
  return `${quotaMenu(o.quota)} + ${spreadMenu(o.spread)}`;
}

/** Voce del menù «PCV»: «20 €/mese». */
export function quotaMenu(quota: string): string {
  return `${corta(quota)} €/mese`;
}

/** Voce del menù «Spread»: in euro col decimale, come nella formula, «0,035 €/kWh» (spec § 8). */
export function spreadMenu(spread: string): string {
  return `${valoreSpread(spread)} €/kWh`;
}

/** PCV in €/mese senza unità (sta nell'intestazione): «20,00». */
export function pcv(x: string): string {
  return euroPortale(x).replace(/ €$/, "");
}

/** Un valore di spread come stampato, senza unità: «0,03», «0,041». */
function valoreSpread(v: string): string {
  const [i, d = ""] = v.trim().split(".");
  return `${i},${d.replace(/0+$/, "").padEnd(2, "0")}`;
}

/** Spread scritto: «0,03» se vale per tutte le fasce, altrimenti «F1F2 0,03 · F3 0,025»; `null` se non è scritto. */
export function spreadScritto(righe: SpreadFascia[] | null): string | null {
  if (righe === null || righe.length === 0) return null;
  if (righe.length === 1 && righe[0].fasce === "tutte") return valoreSpread(righe[0].valore);
  return righe.map((r) => `${r.fasce} ${valoreSpread(r.valore)}`).join(" · ");
}

/** Lo spread non dichiarato calcolato dal conto: «0,090» (tre decimali, come il ricarico al kWh), senza «circa». */
export function spreadCalcolato(v: string): string {
  return v.replace(".", ",");
}

/** «24,00 €: accesso Portale Adempia · IVA 22%»: prima la cifra, poi la voce (Alfonso 24/10: testo e cifre mescolati si
 *  leggevano male); le voci senza cifra in fondo, l'importo solo quando la bolletta lo dà; «—» se non ce ne sono. */
export function altriAddebiti(altri: AltroAddebito[]): string {
  if (altri.length === 0) return "—";
  // dopo la cifra la voce continua la frase: iniziale minuscola, tranne sigle e unità («kWh», «IVA»)
  const voce = (n: string) => (/^[A-Z][a-zàèéìòù]/.test(n) ? n[0].toLowerCase() + n.slice(1) : n);
  const conCifra = altri.filter((a) => a.euro !== null)
    .map((a) => `${a.al_mese ? alMese(a.euro!) : euroPortale(a.euro!)}: ${voce(a.nome)}`);
  const senza = altri.filter((a) => a.euro === null).map((a) => a.nome);
  return conUnita([...conCifra, ...senza].join(" · "));
}

/** Tutte le voci in fila, la percentuale con la parola prima (nella cella della tabella e nella nota della compatta). */
export function altriInFila(altri: AltroAddebito[]): string {
  return altriAddebiti(altri).replace(/ (\d+(?:,\d+)?%)/g, " $1");
}

/** «2,36 € (0,013 €/kWh)»; «almeno 2,39 € (…)» quando il motore dà un minimo. La stima col PUN ora per ora ha il solo
 *  numero (Alfonso 29/10: niente «circa» in un preventivatore). */
export function ricarico(euroRicarico: string, kwh: string | null, minimo = false): string {
  const [importo, perKwh] = ricaricoParti(euroRicarico, kwh, minimo);
  return conUnita(perKwh === null ? importo : `${importo} (${perKwh})`);
}

/** Le due parti di `ricarico`: l'importo (con «almeno») e, se c'è, il prezzo al kWh senza parentesi. */
export function ricaricoParti(euroRicarico: string, kwh: string | null, minimo = false): [string, string | null] {
  const importo = euroPortale(euroRicarico);
  return [conUnita(minimo ? `almeno ${importo}` : importo), kwh === null ? null : conUnita(prezzoUnitario(kwh, "kWh"))];
}

/** Solo gli esiti con almeno una frase, e di ciascuno solo le frasi non vuote: una pillola da sola non si capisce
 *  (spec 2026-10-23 § 2, anti-obiettivo 2). */
export function esitiConFrase(esiti: Esito[]): Esito[] {
  return esiti
    .map((e) => ({ ...e, voci: (e.voci ?? []).filter((v) => v.frase.trim() !== "") }))
    .filter((e) => e.voci.length > 0);
}

/** Gli esiti del gas: come `esitiConFrase`, ma una «da verificare» dell'AI senza motivo ha la sola pillola e il titolo
 *  (frase vuota, decisione del 26/10): la riga gas non deve sembrare «pulita». */
export function esitiGasVisibili(esiti: Esito[]): Esito[] {
  return esiti.filter((e) => (e.voci ?? []).length > 0);
}

/** Il totale di «Tutti i condomìni» (24/10, variante B): la somma delle cifre al mese dei condomìni che hanno
 *  un'offerta e una cifra (le stesse delle righe dei condomìni), la somma delle loro stime annue (solo se ce l'hanno
 *  tutti), quanti sono contati su quanti, le forniture, l'offerta comune (la stessa su tutti, altrimenti `null`: i
 *  menù partono vuoti) e le forniture con un'offerta propria. Le somme sulle cifre, in centesimi, mai sul float. */
export interface Totale {
  mese: string | null;
  anno: string | null;
  contati: number;
  condomini: number;
  forniture: number;
  offerta: string | null;
  diverse: number;
}

function inCentesimi(x: string): bigint {
  const t = x.trim();
  const neg = t.startsWith("-");
  const [i, d = ""] = t.replace(/^[-+]/, "").split(".");
  const c = BigInt(i || "0") * 100n + BigInt((d + "00").slice(0, 2));
  return neg ? -c : c;
}

function daCentesimi(c: bigint): string {
  const neg = c < 0n;
  const a = neg ? -c : c;
  return `${neg ? "-" : ""}${a / 100n}.${String(a % 100n).padStart(2, "0")}`;
}

export function totaleCondomini(tutti: Condominio[]): Totale {
  // un condominio di sole bollette gas non ha offerta né cifre: fuori dal conteggio (giro 1 del gas)
  const condomini = tutti.filter((c) => c.contatori.length > 0);
  const contati = condomini.filter((c) => c.offerta_id !== null && c.mese !== null);
  const somma = (xs: string[]) => daCentesimi(xs.reduce((s, x) => s + inCentesimi(x), 0n));
  const offerte = new Set(condomini.map((c) => c.offerta_id));
  const [comune] = offerte;
  return {
    mese: contati.length > 0 ? somma(contati.map((c) => c.mese!)) : null,
    anno: contati.length > 0 && contati.every((c) => c.anno !== null) ? somma(contati.map((c) => c.anno!)) : null,
    contati: contati.length,
    condomini: condomini.length,
    forniture: condomini.reduce((s, c) => s + c.n_forniture, 0),
    offerta: offerte.size === 1 && comune !== null && comune !== undefined ? comune : null,
    diverse: condomini.reduce((s, c) => s + c.contatori.filter((k) => k.propria).length, 0),
  };
}

/** Tutte le bollette di un condominio, luce e gas: la pagina continua a interrogare il server finché una è in analisi. */
export function bolletteDelCondominio(c: Condominio): BollettaVista[] {
  return [...c.contatori.flatMap((k) => k.bollette), ...(c.contatori_gas ?? []).flatMap((k) => k.bollette)];
}
